import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/transport';
import { highlightElementsGrpc } from '../../../shared/api/grpc/validationGrpcClient';
import type { QuickFillItem } from '../../../shared/types';
import { resolveColumnKey } from '../../../shared/lib/table/columnMapping';
import type { SearchMatch } from '../../../shared/lib/utils/findReplaceUtils';
import isTextInputTarget from '../../../shared/lib/dom/isTextInputTarget';
import {
  areValidationFocusColumnsReadyInData,
  buildQuickFillCellFillDownPlan,
  isValidationFocusDatasetModeReady,
} from '../lib/quickFillPageModel';
import {
  resolveQuickFillModelSelectionRowIndices,
  resolveQuickFillSelectionModelElementIds,
} from '../lib/quickFillModelSelection';
import {
  getQuickFillValueOptions,
  getQuickFillValueOptionsForCategories,
} from '../lib/quickFillValueOptions';
import type { QuickFillPageWorkspaceController } from './useQuickFillPageWorkspaceController';

export function useQuickFillPageInteractionController(
  controller: QuickFillPageWorkspaceController
) {
  const {
    quickFillData, navigation, cellSelection, operations,
    highlightedQuickFillColumnLabels, validationFocus, isTableLoading,
    shouldUseFamilyTypeFallbackForValidation, initialization, revitApi, snackbar,
    visibleProperties, submit,
  } = controller;
  // Owned here: every writer (context-menu open/close, Escape dismissal) lives
  // in this controller or below it.
  const [contextMenu, setContextMenu] = useState({
    open: false,
    position: { x: 0, y: 0 },
    rowIndex: null as number | null,
  });
  const { selectedCells, activeColumn, isValidSelection } = cellSelection;
  const { selectedRows } = operations;
  const [activeFindReplaceMatch, setActiveFindReplaceMatch] =
    useState<SearchMatch | null>(null);
  // Derived navigation state comes from useQuickFillNavigation — recomputing it
  // here would duplicate O(n) work and risk divergent results.
  const { navigationState, filteredRowPositionMap } = navigation;
  const cellSelectedRowIndices = useMemo(() => Array.from(new Set(
    selectedCells.map((cell) => cell.rowIndex).filter(
      (rowIndex) => Number.isInteger(rowIndex) && rowIndex >= 0
    )
  )).sort((left, right) => left - right), [selectedCells]);
  const handleClearCurrentSelection = useCallback(() => {
    if (activeColumn && isValidSelection && cellSelectedRowIndices.length > 0) {
      operations.handleClearSelectedCells({
        rowIndices: cellSelectedRowIndices,
        columnLabels: [activeColumn],
      });
    } else {
      operations.handleClearSelectedCells();
    }
  }, [activeColumn, cellSelectedRowIndices, isValidSelection, operations]);
  const handleCopyCurrentSelection = useCallback(() => {
    if (activeColumn && isValidSelection && selectedCells.length > 0) {
      void operations.handleCopySelected(selectedCells);
    } else {
      void operations.handleCopySelected();
    }
  }, [activeColumn, isValidSelection, operations, selectedCells]);
  const handlePasteCurrentSelection = useCallback(() => {
    if (activeColumn && isValidSelection && selectedCells.length > 0) {
      void operations.handlePaste(selectedCells);
    } else {
      void operations.handlePaste();
    }
  }, [activeColumn, isValidSelection, operations, selectedCells]);
  const handleClearActiveColumn = useCallback(() => {
    if (!activeColumn) return;
    operations.handleClearSelectedCells({
      rowIndices: quickFillData.map((_item, index) => index),
      columnLabels: [activeColumn],
      scope: 'column',
    });
  }, [activeColumn, operations, quickFillData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const targetIsTextEntry = isTextInputTarget(event.target) && (
        !(event.target instanceof HTMLInputElement) || ![
          'button', 'checkbox', 'color', 'file', 'image', 'radio', 'range', 'reset', 'submit',
        ].includes(event.target.type)
      );
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
        if (targetIsTextEntry || (selectedCells.length === 0 && selectedRows.length === 0)) return;
        event.preventDefault();
        handleCopyCurrentSelection();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
        const targetIsSelectedQuickFillCellEditor = event.target instanceof HTMLElement &&
          event.target.closest('[data-quick-fill-cell="true"][data-selected="true"]') !== null;
        if (targetIsTextEntry && !targetIsSelectedQuickFillCellEditor) return;
        event.preventDefault();
        handlePasteCurrentSelection();
        return;
      }
      if (
        (event.key !== 'Delete' && event.key !== 'Backspace') ||
        targetIsTextEntry ||
        (cellSelectedRowIndices.length === 0 && selectedRows.length === 0)
      ) return;
      event.preventDefault();
      handleClearCurrentSelection();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [
    cellSelectedRowIndices.length, handleClearCurrentSelection, handleCopyCurrentSelection,
    handlePasteCurrentSelection,
    selectedCells.length, selectedRows.length,
  ]);

  const fillDownTargetRowIndices = selectedRows.length >= 2
    ? selectedRows
    : cellSelectedRowIndices;
  const cellFillDownPlan = useMemo(() => buildQuickFillCellFillDownPlan({
    selectedCells,
    activeColumn,
    isValidSelection,
    rowCount: quickFillData.length,
  }), [activeColumn, isValidSelection, quickFillData.length, selectedCells]);
  const modelSelectionRowIndices = useMemo(() =>
    resolveQuickFillModelSelectionRowIndices({
      contextMenuOpen: contextMenu.open,
      contextMenuRowIndex: contextMenu.rowIndex,
      selectedCellRowIndices: cellSelectedRowIndices,
      navigationRequestRowIndex: navigation.navigationRequest?.rowIndex ?? null,
      selectedRows,
      rowCount: quickFillData.length,
    }), [
      cellSelectedRowIndices, contextMenu, navigation.navigationRequest?.rowIndex,
      quickFillData.length, selectedRows,
    ]);
  const modelSelectionElementIds = useMemo(
    () => resolveQuickFillSelectionModelElementIds(
      quickFillData,
      modelSelectionRowIndices,
      initialization.pipeElements
    ),
    [initialization.pipeElements, modelSelectionRowIndices, quickFillData]
  );
  const canSelectModelComponents = modelSelectionElementIds.length > 0 &&
    (revitApi.isReady || isGrpcRuntimeAvailable());
  const validationFocusColumnKeys = useMemo(() => Array.from(new Set(
    highlightedQuickFillColumnLabels.map(
      (label) => resolveColumnKey(label) as keyof QuickFillItem
    ).filter(Boolean)
  )), [highlightedQuickFillColumnLabels]);
  const validationFocusColumnsReadyInData = useMemo(
    () => areValidationFocusColumnsReadyInData({
      validationFocusColumnKeys,
      quickFillData,
      isTableLoading,
    }),
    [isTableLoading, quickFillData, validationFocusColumnKeys]
  );
  const validationFocusDatasetModeReady = useMemo(
    () => isValidationFocusDatasetModeReady({
      requestId: validationFocus.activeValidationQuickFillFocus?.requestId,
      preferredRowMode: validationFocus.activeValidationQuickFillFocus?.preferredRowMode,
      quickFillData,
      shouldUseFamilyTypeFallbackForValidation,
    }),
    [
      quickFillData, shouldUseFamilyTypeFallbackForValidation,
      validationFocus.activeValidationQuickFillFocus?.preferredRowMode,
      validationFocus.activeValidationQuickFillFocus?.requestId,
    ]
  );
  const findReplaceVisibleRowIndices = useMemo(
    () => navigation.hasReportedFilteredRows
      ? navigation.filteredRowIndices
      : quickFillData.map((_item, index) => index),
    [navigation.filteredRowIndices, navigation.hasReportedFilteredRows, quickFillData]
  );
  const handleToggleRowMode = useCallback((showEveryInstance: boolean) => {
    controller.manualRowModeOverrideRef.current = true;
    controller.setRowMode(showEveryInstance ? 'instance' : 'familyType');
    controller.setValidationFocusPreset(null);
    operations.replaceSelectedRows([]);
    navigation.setNavigationMarkerRows([]);
    navigation.setNavigationRequest(null);
  }, [controller, navigation, operations]);
  const clearFindReplaceFocus = useCallback(() => {
    setActiveFindReplaceMatch(null);
    navigation.setNavigationRequest(null);
  }, [navigation]);
  const handleFindReplaceHighlight = useCallback((match: SearchMatch | null) => {
    if (!match) {
      clearFindReplaceFocus();
      return;
    }
    setActiveFindReplaceMatch(match);
    navigation.setNavigationRequest((previous) => ({
      rowIndex: match.row,
      columnLabel: match.column,
      requestId: (previous?.requestId ?? 0) + 1,
    }));
  }, [clearFindReplaceFocus, navigation]);
  const handleValueSelect = useCallback((value: string) => {
    if (selectedCells.length === 0 || !isValidSelection || !activeColumn) return;
    const targetRows = Array.from(new Set(selectedCells.filter(
      (cell) => cell.columnLabel === activeColumn
    ).map((cell) => cell.rowIndex))).sort((left, right) => left - right);
    if (targetRows.length > 1) {
      void operations.handleBulkEdit(activeColumn as keyof QuickFillItem, value, targetRows);
    } else if (targetRows.length === 1) {
      void operations.handleTableDataChange(
        targetRows[0], activeColumn as keyof QuickFillItem, value
      );
    }
  }, [activeColumn, isValidSelection, operations, selectedCells]);
  const getColumnUsageStats = useCallback((columnLabel: string) => {
    const stats: Record<string, number> = {};
    quickFillData.forEach((item) => {
      const value = item[columnLabel as keyof QuickFillItem];
      if (typeof value === 'string' && value.trim()) stats[value] = (stats[value] || 0) + 1;
    });
    return stats;
  }, [quickFillData]);
  // Read row categories through a ref so this callback keeps a stable identity
  // across cell edits; memoizedSelectedProperties feeds memoized table rows.
  const quickFillDataRef = useRef(quickFillData);
  useEffect(() => {
    quickFillDataRef.current = quickFillData;
  }, [quickFillData]);
  const getOptionsForProperty = useCallback((label: string, rowIndices: number[] = []) => {
    if (label !== 'OBJECT-TYPE' || rowIndices.length === 0) {
      return getQuickFillValueOptions(label);
    }
    return getQuickFillValueOptionsForCategories(label, rowIndices.map(
      (index) => (quickFillDataRef.current[index] as any)?.__category
    ).filter((category): category is string => typeof category === 'string' && Boolean(category.trim())));
  }, []);
  const memoizedSelectedProperties = useMemo(() => visibleProperties.map((property) => ({
    label: property.label,
    value: property.value,
    revitParameter: (property as any).revitParameter,
    options: getOptionsForProperty(property.label),
    checked: true,
    isReadOnly: (property as any).isReadOnly || false,
    isInstance: (property as any).isInstance,
  })), [getOptionsForProperty, visibleProperties]);
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const target = event.target instanceof HTMLElement ? event.target : null;
    const raw = target?.closest('[data-row-index]')?.getAttribute('data-row-index');
    const rowIndex = raw ? Number.parseInt(raw, 10) : Number.NaN;
    setContextMenu({
      open: true,
      position: { x: event.clientX, y: event.clientY },
      rowIndex: Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : null,
    });
  }, [setContextMenu]);
  const handleContextMenuClose = useCallback(() => setContextMenu({
    open: false,
    position: { x: 0, y: 0 },
    rowIndex: null,
  }), [setContextMenu]);
  const handleSelectModelComponents = useCallback(async () => {
    if (modelSelectionElementIds.length === 0) {
      snackbar.showWarning('Select an instance row or a family/type row with project instances first.');
      return;
    }
    if (!revitApi.isReady && !isGrpcRuntimeAvailable()) {
      snackbar.showWarning('Revit selection is unavailable until the model connector is ready.');
      return;
    }
    try {
      const result = isGrpcRuntimeAvailable()
        ? await highlightElementsGrpc(modelSelectionElementIds.map((id) => BigInt(id)))
        : await revitApi.call('validation.highlightElements', {
          elementIds: JSON.stringify(modelSelectionElementIds),
        });
      result?.success
        ? snackbar.showSuccess(`Selected ${result.count ?? modelSelectionElementIds.length} component(s) in Revit.`)
        : snackbar.showError(result?.error || 'Failed to select components in Revit.');
    } catch (error) {
      snackbar.showError(error instanceof Error ? error.message : 'Failed to select components in Revit.');
    }
  }, [modelSelectionElementIds, revitApi, snackbar]);
  const handleFillDownClick = useCallback(async () => {
    if (selectedRows.length >= 2) return operations.handleFillDown();
    if (!cellFillDownPlan) {
      snackbar.showWarning('Select a cell in the column before using Fill down.');
      return;
    }
    const value = quickFillData[cellFillDownPlan.sourceRowIndex]?.[
      cellFillDownPlan.columnLabel as keyof QuickFillItem
    ];
    if (value == null || !String(value).trim()) {
      snackbar.showWarning('The source cell is empty, so Fill down has no value to copy.');
      return;
    }
    await operations.handleBulkEdit(
      cellFillDownPlan.columnLabel as keyof QuickFillItem,
      String(value),
      cellFillDownPlan.targetRowIndices
    );
  }, [cellFillDownPlan, operations, quickFillData, selectedRows.length, snackbar]);
  const handleQuickFillRowChange = useCallback((
    index: number, field: keyof QuickFillItem, value: string
  ) => {
    const key = `${index}-${String(field)}`;
    submit.setFailedCellErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    void operations.handleTableDataChange(index, field, value);
  }, [operations, submit]);

  return {
    ...controller,
    contextMenu,
    setContextMenu,
    navigationState,
    filteredRowPositionMap,
    cellSelectedRowIndices,
    handleClearCurrentSelection,
    handleCopyCurrentSelection,
    handlePasteCurrentSelection,
    handleClearActiveColumn,
    fillDownTargetRowIndices,
    cellFillDownPlan,
    modelSelectionElementIds,
    canSelectModelComponents,
    hiddenMarkerCount: navigation.markerItems.filter((marker) => !marker.isVisible).length,
    isValidationScopedView: Boolean(
      validationFocus.validationScopeInfo?.requestId &&
      validationFocus.effectiveValidationScope?.requestId
    ),
    validationFocusColumnKeys,
    validationFocusColumnsReadyInData,
    validationFocusDatasetModeReady,
    findReplaceVisibleRowIndices,
    handleToggleRowMode,
    handleDismissValidationScope: validationFocus.handleDismissValidationScope,
    clearFindReplaceFocus,
    handleFindReplaceHighlight,
    handleValueSelect,
    handleCloseValueSelector: cellSelection.clearSelection,
    getColumnUsageStats,
    getOptionsForProperty,
    memoizedSelectedProperties,
    handleContextMenu,
    handleContextMenuClose,
    handleSelectModelComponents,
    activeFindReplaceMatch,
    handleFillDownClick,
    handleQuickFillRowChange,
  };
}

export type QuickFillPageInteractionController = ReturnType<
  typeof useQuickFillPageInteractionController
>;
